import type { ReactNode } from 'react'
import { Copy, Eye, FileText, MessageSquare } from 'lucide-react'
import { ADD_TO_CURRENT_CHAT_LABEL, AddToCurrentChatIcon } from '@/lib/add-to-current-chat'
import { userFacingCopyPath } from '@/lib/app-routes'
import type { Workspace } from '@/providers/workspace-provider'
import { testIds } from '@/lib/test-ids'

export type HomeFeedMenuItem =
  | { separator: true }
  | { icon: ReactNode; label: string; action: () => void; testId?: string }

export type HomeFeedContextMenuProps = {
  x: number
  y: number
  onClose: () => void
  onOpen: () => void
  onPreview: () => void
  /**
   * When set, replaces the default single "Open" item (e.g. chat surface choices:
   * Open in new tab / Open in agent panel).
   */
  primaryActions?: HomeFeedMenuItem[]
  /** When false, hide Preview (default true). */
  showPreview?: boolean
  /** When set, show Add to current chat */
  addToChat?: { id: string; title: string; path?: string } | null
  /** Raw storage path for copy (clipboard gets slug path via userFacingCopyPath) */
  filePath?: string | null
  workspaces?: Workspace[]
  /** Extra items after Open / Preview / Add to chat (e.g. Open Inbox) */
  extraItems?: HomeFeedMenuItem[]
}

export function HomeFeedContextMenu({
  x,
  y,
  onClose,
  onOpen,
  onPreview,
  primaryActions,
  showPreview = true,
  addToChat,
  filePath,
  workspaces = [],
  extraItems = [],
}: HomeFeedContextMenuProps) {
  const routeWorkspaces = workspaces.map((w) => ({
    id: w.id,
    slug: w.slug,
    contentRoot: w.contentRoot,
    name: w.name,
  }))
  const userFacingPath = filePath
    ? userFacingCopyPath(filePath, routeWorkspaces)
    : null
  const filename = filePath?.split('/').filter(Boolean).pop() ?? null

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      /* ignore */
    }
    onClose()
  }

  const items: HomeFeedMenuItem[] = primaryActions?.length
    ? [...primaryActions]
    : [
        {
          icon: <FileText className="h-3.5 w-3.5" />,
          label: 'Open',
          action: () => {
            onOpen()
            onClose()
          },
          testId: testIds.home.contextMenuOpen,
        },
      ]

  if (showPreview) {
    items.push({
      icon: <Eye className="h-3.5 w-3.5" />,
      label: 'Preview',
      action: () => {
        onPreview()
        onClose()
      },
      testId: testIds.home.contextMenuPreview,
    })
  }

  if (addToChat) {
    items.push({
      icon: <AddToCurrentChatIcon className="h-3.5 w-3.5" />,
      label: ADD_TO_CURRENT_CHAT_LABEL,
      action: () => {
        window.dispatchEvent(
          new CustomEvent('add-to-chat', {
            detail: {
              type: 'document',
              id: addToChat.id,
              title: addToChat.title,
              name: addToChat.title,
              path: addToChat.path,
            },
          }),
        )
        onClose()
      },
      testId: testIds.home.contextMenuAddToChat,
    })
  }

  if (extraItems.length > 0) {
    items.push({ separator: true }, ...extraItems)
  }

  if (filename || userFacingPath) {
    items.push({ separator: true })
    if (filename) {
      items.push({
        icon: <Copy className="h-3.5 w-3.5" />,
        label: 'Copy filename',
        action: () => handleCopy(filename),
      })
    }
    if (userFacingPath) {
      items.push({
        icon: <Copy className="h-3.5 w-3.5" />,
        label: 'Copy path',
        action: () => handleCopy(userFacingPath),
      })
    }
  }

  const left = Math.min(x, window.innerWidth - 220)
  const top = Math.min(y, window.innerHeight - items.length * 32 - 8)

  return (
    <div
      className="fixed z-[200] min-w-[200px] rounded-md border border-border bg-popover shadow-md py-1 text-sm"
      style={{ left, top }}
      data-testid={testIds.home.contextMenu}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {items.map((item, i) =>
        'separator' in item ? (
          <div key={i} className="my-1 border-t border-border" />
        ) : (
          <button
            key={i}
            type="button"
            data-testid={item.testId}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
            onClick={item.action}
          >
            <span className="text-muted-foreground">{item.icon}</span>
            {item.label}
          </button>
        ),
      )}
    </div>
  )
}

/** Helper icon for "Continue in chat" style extras */
export function HomeFeedChatMenuIcon() {
  return <MessageSquare className="h-3.5 w-3.5" />
}
